import {AddressResponseData, CountriesFactory} from "@connox/typescript-library";
import * as React from "react";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {getShopId} from "../../../lib/getShopId";
import window, {IAmazonError} from "../../../lib/WindowExtended";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {isSameAddresses} from "../AddressStep/AddressStep";
import {amazonPayAddressReadableContainerId, widgetOptions} from "../../../lib/payments/AmazonPayLibrary";

interface ISummaryAddressProps {
  goToAddressView: () => void;
  onError: (error: IAmazonError) => void;
}

const SummaryAddress: React.FC<ISummaryAddressProps> = (props) => {
  const {checkout} = useCheckoutContext();
  const {t} = useTranslation(["checkout"]);
  const {deliveryAddress, invoiceAddress} = checkout;

  const [deliveryCountry, setDeliveryCountry] = useState<string>("");
  const [invoiceCountry, setInvoiceCountry] = useState<string>("");

  const {goToAddressView} = props;

  useEffect(() => {
    if (invoiceAddress && deliveryAddress) {
      void CountriesFactory.getAllCountries(getShopId()).then(() => {
        const invoiceCountryLookup = CountriesFactory.getCountryByCode(invoiceAddress.countryCode);
        setInvoiceCountry(invoiceCountryLookup ? invoiceCountryLookup.name : "");

        const deliveryCountryLookup = CountriesFactory.getCountryByCode(deliveryAddress.countryCode);
        setDeliveryCountry(deliveryCountryLookup ? deliveryCountryLookup.name : "");
      });
    }
    if (window.isBillingAgreementAddressSet && window.amazon) {
      new window.OffAmazonPayments.Widgets.AddressBook({
        amazonOrderReferenceId: window.orderReferenceId,
        onError: props.onError,
        ...widgetOptions,
        displayMode: "Read",
      }).bind(amazonPayAddressReadableContainerId);
    }
  }, []);

  // delivery address paragraph preparation
  const DeliveryParagraph: React.FC = () => {
    const Content: React.FC = () => {
      if (window.isBillingAgreementAddressSet) {
        // Return Amazon Pay address widget
        return (
          <React.Fragment>
            <div id={amazonPayAddressReadableContainerId} style={{height: "150px", marginBottom: "1rem"}}>
              {/* filled by amazon pay */}
            </div>
            <p className="Summary__Address">
              {t("E-Mail: {{email}}", {email: checkout.deliveryAddress?.email})}
              {checkout.deliveryAddress?.phone && (
                <React.Fragment>
                  <br />
                  {t("Tel.: {{phone}}", {phone: checkout.deliveryAddress.phone})}
                </React.Fragment>
              )}
            </p>
          </React.Fragment>
        );
      } else if (deliveryAddress) {
        // return internal address
        if (isSameAddresses(deliveryAddress, invoiceAddress)) {
          // if same as invoice show notice
          return <p className="Summary__Address">{t("Entspricht der Rechnungsadresse")}</p>;
        } else {
          // if not, show delivery address
          return <Paragraph address={deliveryAddress} humanReadableCountry={deliveryCountry} isInvoice={false} />;
        }
      } else {
        // if no address given, show request (should never be the case)
        return <p className="Summary__Address">{t("Sie müssen eine Lieferadresse angeben")}</p>;
      }
    };

    return (
      <AddressWrapper goToAddressView={goToAddressView} title={t("Lieferadresse")}>
        <Content />
      </AddressWrapper>
    );
  };

  // invoice address paragraph preparation
  const InvoiceParagraph: React.FC = () => {
    const Content: React.FC = () => {
      if (window.isBillingAgreementAddressSet) {
        // if amazon pay, show same-as-delivery notice
        return <p className="Summary__Address">{t("Entspricht der Lieferadresse")}</p>;
      } else if (invoiceAddress) {
        // internal address if given
        return <Paragraph address={invoiceAddress} humanReadableCountry={invoiceCountry} isInvoice={true} />;
      } else {
        // show request to fill in invoice address if not given (should never be the case)
        return <p className="Summary__Address">{t("Sie müssen eine Rechnungsadresse angeben")}</p>;
      }
    };

    return (
      <AddressWrapper goToAddressView={goToAddressView} title={t("Rechnungsadresse")}>
        <Content />
      </AddressWrapper>
    );
  };

  return window.isBillingAgreementAddressSet ? (
    <React.Fragment>
      <DeliveryParagraph />
      <InvoiceParagraph />
    </React.Fragment>
  ) : (
    <React.Fragment>
      <InvoiceParagraph />
      <DeliveryParagraph />
    </React.Fragment>
  );
};

export default SummaryAddress;

/**
 * React address container
 * @param props
 * @constructor
 */
const AddressWrapper: React.FC<{goToAddressView: () => void; title: string}> = (props) => {
  return (
    <React.Fragment>
      <h2>{props.title}</h2>
      <span className="Pen" onClick={props.goToAddressView}>
        <span className="Svg Svg-Icon-Pen">&#160;</span>
      </span>
      <div className="ArrowBorder">{props.children}</div>
    </React.Fragment>
  );
};

/**
 * React address paragraph
 * @param props
 * @constructor
 */
const Paragraph: React.FC<{
  address: AddressResponseData;
  humanReadableCountry?: string;
  isInvoice: boolean;
}> = (props) => {
  const {address, humanReadableCountry, isInvoice} = props;
  const {t} = useTranslation("checkout");
  const {city, company, department, email, firstName, lastName, phone, state, street, zip} = address;
  const streetNo = props.address.number;

  return (
    <p className="Summary__Address">
      {company && (
        <>
          {company}
          <br />
        </>
      )}
      {department && (
        <>
          {department}
          <br />
        </>
      )}
      {`${firstName} ${lastName}`}
      <br />
      {`${street} ${streetNo}`}
      <br />
      {`${zip} ${city}`}
      <br />
      {!!state ? `${state}, ${humanReadableCountry || ""}` : humanReadableCountry}
      {isInvoice && (
        <>
          <br />
          {t("E-Mail: {{email}}", {email})}
        </>
      )}
      {phone && (
        <>
          <br />
          {t("Tel.: {{phone}}", {phone})}
        </>
      )}
    </p>
  );
};
